import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import 'country-flag-icons/3x2/flags.css';
import useClickOutside from 'utils/useClickOutside';
import { Country } from 'utils/country';
import { handleDropdownKeyDown } from 'utils/keyboard';
import {
  CountrySelectorContainer,
  CountrySelectorButton,
  DropdownArrow,
  CountryDropdownList,
  CountryOption,
} from './styles';

interface CountrySelectorProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  countries: Country[];
  selectedCountry: Country;
  onCountryChange: (country: Country) => void;
  disabled?: boolean;
}

const CountrySelector: React.FC<CountrySelectorProps> = ({
  countries,
  selectedCountry,
  onCountryChange,
  disabled = false,
  ...props
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useClickOutside(containerRef, () => setIsOpen(false));

  const toggleDropdown = useCallback(() => {
    if (disabled) return;
    setIsOpen((prev) => !prev);
  }, [disabled]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    handleDropdownKeyDown(e, {
      disabled,
      items: countries,
      activeIndex,
      setActiveIndex,
      onSelect: onCountryChange,
      closeDropdown: () => setIsOpen(false),
      focusButton: () => buttonRef.current?.focus(),
    });
  };

  useEffect(() => {
    if (isOpen && !disabled) {
      const selectedIndex = countries.findIndex(
        (c) => c.iso2 === selectedCountry.iso2,
      );
      setActiveIndex(selectedIndex);
      listRef.current?.focus();
    }
  }, [isOpen, countries, selectedCountry, disabled]);

  useEffect(() => {
    if (activeIndex >= 0) {
      const optionEl = listRef.current?.children[activeIndex] as HTMLLIElement;
      optionEl?.scrollIntoView({ block: 'nearest' });
    }
  }, [activeIndex]);

  return (
    <CountrySelectorContainer ref={containerRef}>
      <CountrySelectorButton
        ref={buttonRef}
        type="button"
        onClick={toggleDropdown}
        aria-expanded={isOpen}
        aria-controls="country-dropdown-list"
        aria-label={`${t(
          'selectCountryCodeForPhoneNumber',
          'Select country code',
        )}, ${selectedCountry.name} ${selectedCountry.dialCode}`}
        aria-haspopup="listbox"
        disabled={disabled}
        {...props}
      >
        <span className={`flag flag:${selectedCountry.flag.toUpperCase()}`} />
        <DropdownArrow>{isOpen ? '▲' : '▼'}</DropdownArrow>
      </CountrySelectorButton>

      {isOpen && !disabled && (
        <CountryDropdownList
          ref={listRef}
          id="country-dropdown-list"
          role="listbox"
          tabIndex={-1}
          onKeyDown={handleKeyDown}
          aria-activedescendant={
            activeIndex >= 0
              ? `country-option-${countries[activeIndex].iso2}`
              : undefined
          }
        >
          {countries.map((country, index) => (
            <CountryOption
              key={country.iso2}
              id={`country-option-${country.iso2}`}
              role="option"
              aria-selected={selectedCountry.iso2 === country.iso2}
              className={`${
                selectedCountry.iso2 === country.iso2 ? 'selected' : ''
              } ${index === activeIndex ? 'active' : ''}`}
              onClick={() => {
                onCountryChange(country);
                setIsOpen(false);
                buttonRef.current?.focus();
              }}
            >
              <span className={`flag flag:${country.flag.toUpperCase()}`} />
              <span className="country-name">{country.name}</span>
              <span className="country-dial-code">{country.dialCode}</span>
            </CountryOption>
          ))}
        </CountryDropdownList>
      )}
    </CountrySelectorContainer>
  );
};

export default CountrySelector;
